import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { api as rootApi } from "@/lib/api";
import { toast } from "sonner";
import { usePrintFit } from "@/dealership/lib/printFit";
import { loadDealer, saveDealer, defaultDealer } from "@/dealership/lib/dealer";
import { BillOfSaleDocument } from "@/dealership/components/BillOfSaleDocument";
import { mergeTexts, toggleCheck } from "@/dealership/lib/billOfSaleText";
import SignSpot from "@/dealership/components/SignSpot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Printer, Settings2 } from "lucide-react";

/** Fillable signing spots overlaid on one page of the live Bill of Sale. */
function SignOverlay({ pageNum, template, deal, onSign }) {
  const signerOf = (id) => (template.signers || []).find((s) => s.id === id);
  const repImg = deal.rep_signature?.image || "";
  return (
    <>
      {(template.spots || []).filter((s) => s.page === pageNum).map((s) => {
        const isRepSig = s.signer_id === "dealer" && s.type === "signature";
        return (
          <SignSpot
            key={s.id}
            spot={s}
            signer={signerOf(s.signer_id)}
            value={deal.spot_signatures?.[s.id]}
            autofillImage={isRepSig ? repImg : ""}
            onSign={(img) => onSign(s, img)}
          />
        );
      })}
    </>
  );
}

export default function BillOfSale() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dealer, setDealer] = useState(loadDealer());
  const [editDealer, setEditDealer] = useState(false);
  const [sigOverride, setSigOverride] = useState("");

  const { data: d, isLoading } = useQuery({
    queryKey: ["deal", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}`)).data,
  });

  const { data: reps = [] } = useQuery({
    queryKey: ["sig-consented"],
    queryFn: async () => (await rootApi.get("/signatures/consented-users")).data,
  });

  const { data: template } = useQuery({
    queryKey: ["bos-settings"],
    queryFn: async () => (await api.get("/bill-of-sale/settings")).data,
  });

  const signMut = useMutation({
    mutationFn: async ({ spot, image }) =>
      (await api.post(`/deals/${dealId}/sign-spot`, {
        spot_id: spot.id,
        signer_id: spot.signer_id,
        image,
        // SALES-024 follow-up: send the wording + layout on display so the FIRST
        // signature freezes it onto the deal. The backend writes it write-once.
        snapshot: snapshotToFreeze,
      })).data,
    onSuccess: (data) => {
      qc.setQueryData(["deal", dealId], (old) => ({ ...data, rep_signature: old?.rep_signature }));
      toast.success("Saved");
    },
    onError: () => toast.error("Could not save"),
  });

  // TICKET-DEAL-001 — page-3 ticks and both note fields save through the
  // dedicated route, which refuses the change once the deal is signed. The write
  // is OPTIMISTIC: the tick/text shows immediately and the save runs in the
  // background, rolling back (with a toast) if it fails.
  const patchMut = useMutation({
    mutationFn: async (fields) => (await api.patch(`/deals/${dealId}/bos-fields`, fields)).data,
    onMutate: async (fields) => {
      await qc.cancelQueries({ queryKey: ["deal", dealId] });
      const prev = qc.getQueryData(["deal", dealId]);
      qc.setQueryData(["deal", dealId], (old) => (old ? { ...old, ...fields } : old));
      return { prev };
    },
    onError: (e, _fields, ctx) => {
      if (ctx?.prev) qc.setQueryData(["deal", dealId], ctx.prev);
      toast.error(e?.response?.data?.detail || "Could not save");
    },
    onSuccess: (data) => qc.setQueryData(["deal", dealId], (old) => ({ ...data, rep_signature: old?.rep_signature })),
  });

  const onToggleCheck = (groupKey, itemId) =>
    patchMut.mutate({ bos_checks: toggleCheck(groupKey, d?.bos_checks, itemId) });
  const onCommitNotes = (val) => patchMut.mutateAsync({ notes: val });
  const onCommitDetails = (val) => patchMut.mutateAsync({ bos_details: val });
  // DEAL-001 follow-up — service plan + F&I line, same optimistic PATCH route.
  const onSetServicePlan = (val) => patchMut.mutate({ service_plan: val });
  const onToggleFi = () => patchMut.mutate({ fi_show: !(d?.fi_show !== false) });
  const onCommitFiName = (val) => patchMut.mutateAsync({ fi_manager_override: val });

  const saveDealerInfo = () => {
    saveDealer(dealer);
    setEditDealer(false);
  };

  const applyOverride = async (uid) => {
    const v = uid === "auto" ? "" : uid;
    setSigOverride(v);
    const resp = await api.get(`/deals/${dealId}`, { params: v ? { sig_override: v } : {} });
    qc.setQueryData(["deal", dealId], (old) => (old ? { ...old, rep_signature: resp.data.rep_signature } : old));
  };

  const handlePrint = () => {
    rootApi.post("/signatures/applied", { doc_type: "bill_of_sale", doc_id: dealId, override_user_id: sigOverride }).catch(() => {});
    window.print();
  };

  usePrintFit(d);

  if (isLoading || !d) {
    return <div className="p-8 text-muted-foreground">Loading bill of sale…</div>;
  }

  const tmpl = template || { signers: [], spots: [], texts: {} };

  // SALES-024 follow-up — which wording/layout this Bill of Sale renders:
  //  • a deal WITH a snapshot uses it verbatim (the wording it was signed under);
  //  • a deal that is signed but has NO snapshot (signed before this feature)
  //    uses the built-in DEFAULTS, never the current custom settings;
  //  • an unsigned deal uses the current settings, as before.
  // Tokens like {{buyer_name}} are still filled from the deal at render time;
  // only the wording is frozen.
  const snap = d.bos_snapshot && (d.bos_snapshot.texts || d.bos_snapshot.spots) ? d.bos_snapshot : null;
  const isSigned =
    Object.values(d.spot_signatures || {}).some(Boolean) || d.signature_buyer || d.signature_dealer;

  // TICKET-DEAL-001 — which version of page 3 to render, keyed off the snapshot:
  //  • unsigned              → new per-item checkboxes, editable;
  //  • signed, new snapshot  → new checkboxes, READ-ONLY (labels from snapshot);
  //  • signed, old/no snapshot → original combined wording, exactly as signed.
  const p3Mode = snap ? (Number(snap.p3_version) >= 2 ? "new" : "legacy") : (isSigned ? "legacy" : "new");
  const editable = !isSigned;

  // DEAL-001 follow-up — letterhead + F&I line. All five letterhead fields (name,
  // address, reg, HST#, F&I) come from THIS browser's localStorage Dealer Info,
  // so a signed Bill of Sale must show the letterhead it was signed with:
  //  • signed with a frozen letterhead → use the snapshot;
  //  • signed before this change (no letterhead) → read Dealer Info, as today;
  //  • unsigned → read Dealer Info + the deal's own fi_show / fi_manager_override.
  const frozenLetter = snap && snap.letterhead && Object.keys(snap.letterhead).length ? snap.letterhead : null;
  const effectiveDealer = frozenLetter
    ? {
        ...dealer,
        name: frozenLetter.name ?? dealer.name,
        address: frozenLetter.address ?? dealer.address,
        reg: frozenLetter.reg ?? dealer.reg,
        hst_number: frozenLetter.hst_number ?? dealer.hst_number,
      }
    : dealer;
  const fiShow = frozenLetter ? frozenLetter.fi_show !== false : d.fi_show !== false;
  const fiName = frozenLetter ? (frozenLetter.fi_name || "") : (d.fi_manager_override || dealer.fi_manager || "");

  let renderTexts, renderTemplate;
  if (snap) {
    renderTexts = snap.texts || {};
    renderTemplate = { signers: snap.signers || [], spots: snap.spots || [] };
  } else {
    // No snapshot: signers + spots come from the CURRENT settings either way —
    // spots were already customisable before this feature, so this is the layout
    // the deal was signed on and whose spot ids match its saved signatures.
    // Rendering defaults here would drop signatures on custom spots. Only the
    // WORDING differs: a deal signed before this change shows the built-in
    // DEFAULT wording (no custom wording existed then); an unsigned deal shows
    // the current custom wording, as before.
    renderTexts = isSigned ? {} : tmpl.texts;
    renderTemplate = { signers: tmpl.signers || [], spots: tmpl.spots || [] };
  }

  // What the first signature will freeze: the full resolved wording + the layout
  // currently on display (an unsigned deal shows the current settings), tagged as
  // the new per-item page-3 version so the renderer can tell it apart later.
  const snapshotToFreeze = {
    texts: mergeTexts(tmpl.texts),
    signers: tmpl.signers || [],
    spots: tmpl.spots || [],
    p3_version: 2,
    // Freeze the whole letterhead as displayed — every field here comes from this
    // browser's localStorage Dealer Info, so without freezing a signed Bill of
    // Sale would show whatever letterhead the viewing browser happens to hold.
    letterhead: {
      name: dealer.name || "",
      address: dealer.address || "",
      reg: dealer.reg || "",
      hst_number: dealer.hst_number || "",
      fi_show: d.fi_show !== false,
      fi_name: d.fi_show !== false ? (d.fi_manager_override || dealer.fi_manager || "") : "",
    },
  };

  return (
    <div className="bos-light print-sheet min-h-screen bg-zinc-100">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-30 bg-white border-b border-border px-6 py-3 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/dealership/deals")} data-testid="bill-back-button">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Deals
        </Button>
        <div className="flex gap-2 items-center">
          {reps.length > 0 && (
            <Select value={sigOverride || "auto"} onValueChange={applyOverride}>
              <SelectTrigger className="w-52 h-9" data-testid="bill-sig-override"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Signing rep: Auto (salesperson)</SelectItem>
                {reps.map((r) => <SelectItem key={r.user_id} value={r.user_id}>Signed by {r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => navigate("/dealership/settings")} data-testid="bill-signing-settings-button">
            <Settings2 className="h-4 w-4 mr-2" /> Signing spots
          </Button>
          <Button variant="outline" onClick={() => setEditDealer((v) => !v)} data-testid="bill-edit-dealer-button">
            <Settings2 className="h-4 w-4 mr-2" /> Dealer Info
          </Button>
          <Button onClick={handlePrint} data-testid="bill-print-button">
            <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {editDealer && (
        <div className="no-print max-w-3xl mx-auto mt-4 bg-white border border-border rounded-md p-5">
          <h3 className="font-heading font-bold mb-3">Edit Dealer Details (saved on this device)</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(defaultDealer).map((k) => (
              <div key={k}>
                <label className="text-xs text-zinc-500 capitalize">{k.replace(/_/g, " ")}</label>
                <Input placeholder={k} value={dealer[k]} onChange={(e) => setDealer({ ...dealer, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end"><Button onClick={saveDealerInfo} data-testid="save-dealer-button">Save</Button></div>
        </div>
      )}

      <div data-testid="bill-of-sale-document">
        <BillOfSaleDocument
          d={d}
          dealer={effectiveDealer}
          texts={renderTexts}
          p3Mode={p3Mode}
          editable={editable}
          fiShow={fiShow}
          fiName={fiName}
          dealerFiDefault={dealer.fi_manager || ""}
          onToggleCheck={onToggleCheck}
          onCommitNotes={onCommitNotes}
          onCommitDetails={onCommitDetails}
          onSetServicePlan={onSetServicePlan}
          onToggleFi={onToggleFi}
          onCommitFiName={onCommitFiName}
          overlay={(n) => (
            <SignOverlay pageNum={n} template={renderTemplate} deal={d} onSign={(spot, image) => signMut.mutate({ spot, image })} />
          )}
        />
      </div>
    </div>
  );
}
